import {Injectable, OnDestroy} from "@angular/core";
import {NavigationEnd, Router} from "@angular/router";
import {Subject, timer} from "rxjs";
import {take, takeUntil} from "rxjs/operators";
import {routes} from "../../../../environments/routes";
import {SimClientStorageService} from "./sim-client-storage.service";

@Injectable({
	providedIn: "root",
})
export class SimIdleReloadService implements OnDestroy {
	private shouldRun: boolean = true;
	private currentUrl: string = "";
	private untilObs = new Subject<void>();

	private readonly sessionKey = "idle-reload";
	private readonly idleRoutes = [routes.routes.eventDashboard, routes.routes.simPlayer, routes.routes.login, routes.routes.display];

	constructor(
		private router: Router,
		private simClientStorageService: SimClientStorageService,
	) {
		document.addEventListener("click", () => {
			this.restartTimer();
		});
		this.router.events.subscribe((evt) => {
			if (evt instanceof NavigationEnd) {
				this.handleNavigation(evt);
			}
		});
	}

	private handleNavigation(evt: NavigationEnd) {
		let uri = new URL(evt.urlAfterRedirects, location.origin);
		let newUrl = uri.pathname.slice(1);

		if (newUrl !== this.currentUrl) {
			this.currentUrl = newUrl;
			this.shouldRun = true;
			this.restartTimer();
		}
	}

	private restartTimer(): void {
		this.debug(`restartTimer`, {
			currentUrl: this.currentUrl,
			shouldRun: this.shouldRun,
			isIdleUrl: this.isIdleUrl,
		});

		this.stopTimer();
		if (!(this.shouldRun && this.isIdleUrl)) {
			return;
		}

		let data = this.simClientStorageService.getSessionData(this.sessionKey);
		this.simClientStorageService.removeSessionData(this.sessionKey);
		//initial waiting period: 20min, 3h after reload
		let waitPeriod = (data ? 180 : 20) * 60 * 1000;
		this.debug("WaitPeriod: " + waitPeriod);

		timer(waitPeriod)
			//https://designtechworld.medium.com/rxjs-avoiding-takeuntil-leaks-d141a27e8d0c
			.pipe(takeUntil(this.untilObs.pipe(take(1))))
			.subscribe(() => {
				this.simClientStorageService.setSessionData(this.sessionKey, "1");
				window.location.reload();
				this.debug("reloading!");
			});
	}

	private stopTimer(): void {
		this.debug(`stopInternal`);
		this.untilObs.next();
	}

	private get isIdleUrl() {
		return this.idleRoutes.findIndex((route) => this.currentUrl.startsWith(route)) >= 0;
	}

	public activate(): void {
		if (!this.shouldRun) {
			this.debug(`activated`);
			this.shouldRun = true;
			this.restartTimer();
		} else {
			this.debug(`already activated`);
		}
	}

	public deactivate(): void {
		if (this.shouldRun) {
			this.debug(`deactivated`);
			this.shouldRun = false;
			this.stopTimer();
		} else {
			this.debug(`already deactivated`);
		}
	}

	ngOnDestroy(): void {
		this.stopTimer();
		this.deactivate();
	}

	private debug(message: string, ...params: any[]) {
		//console.debug(`SimIdleReloadService: ${message}`, params);
	}
}
